/**
 * @file ReportShell.tsx
 * @brief Unified report chrome for Super + Company Admin (Phase C).
 *
 * @details
 * Range bar, optional company filter, catalog back-link, and export actions.
 * Prefer `serverExport` for CSV/PDF (server-side); `onExportCsv` remains for
 * client-only composition fallbacks. Layout uses GridBlock flush to the rail;
 * report bodies render KPIs / tables as sibling `GridCell`s (not inset cards).
 * Filters sit in flush `GridCells` (same pattern as list pages).
 *
 * @author DES
 * @date 27-07-2026
 * @updated 29-07-2026 — flush GridCell range + company filters
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import {
  AnalyticsRangeBar,
  flatAnalyticsFilterControlClass,
  type AnalyticsRange as RangeBarValue,
} from "@/features/analytics/components/AnalyticsRangeBar";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { toast } from "@/shared/hooks/useToast";
import {
  downloadReportExport,
  type ServerReportExport,
} from "./downloadReportExport";

export type ReportShellProps = {
  catalogTo: "/admin/reports" | "/company/reports";
  title: string;
  description: string;
  range: RangeBarValue;
  onRangeChange: (next: RangeBarValue) => void;
  showRangeBar?: boolean;
  companyId?: string | null;
  onCompanyChange?: (id: string | null) => void;
  showCompanyFilter?: boolean;
  /** Client-side CSV (legacy / composition fallback). */
  onExportCsv?: () => void | Promise<void>;
  /** Server-side CSV (+ optional PDF). */
  serverExport?: ServerReportExport;
  enablePdf?: boolean;
  exportDisabled?: boolean;
  children: ReactNode;
};

function CompanyFilterCell({
  companyId,
  onCompanyChange,
}: {
  companyId: string | null;
  onCompanyChange: (id: string | null) => void;
}) {
  return (
    <GridCell padded={false}>
      <CompanyPicker
        value={companyId}
        onChange={onCompanyChange}
        placeholder="Select a company…"
        size="sm"
        triggerClassName={flatAnalyticsFilterControlClass}
      />
    </GridCell>
  );
}

export function ReportShell({
  catalogTo,
  title,
  description,
  range,
  onRangeChange,
  showRangeBar = true,
  companyId = null,
  onCompanyChange,
  showCompanyFilter = false,
  onExportCsv,
  serverExport,
  enablePdf = false,
  exportDisabled = false,
  children,
}: ReportShellProps) {
  const [busy, setBusy] = useState<"csv" | "pdf" | null>(null);

  const runCsv = async () => {
    if (exportDisabled || busy) return;
    setBusy("csv");
    try {
      if (serverExport) {
        await downloadReportExport(serverExport, "csv");
      } else if (onExportCsv) {
        await onExportCsv();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not export CSV";
      toast({
        variant: "destructive",
        title: "Export failed",
        description: message,
      });
    } finally {
      setBusy(null);
    }
  };

  const runPdf = async () => {
    if (!serverExport || exportDisabled || busy) return;
    setBusy("pdf");
    try {
      await downloadReportExport(serverExport, "pdf");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not export PDF";
      toast({
        variant: "destructive",
        title: "Export failed",
        description: message,
      });
    } finally {
      setBusy(null);
    }
  };

  const canCsv = Boolean(serverExport || onExportCsv);
  const companyTrailing =
    showCompanyFilter && onCompanyChange ? (
      <CompanyFilterCell
        companyId={companyId}
        onCompanyChange={onCompanyChange}
      />
    ) : null;

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <Link
              to={catalogTo}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All reports
            </Link>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {canCsv ? (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={exportDisabled || busy !== null}
                onClick={() => void runCsv()}
              >
                {busy === "csv" ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export CSV
              </Button>
            ) : null}
            {enablePdf && serverExport ? (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={exportDisabled || busy !== null}
                onClick={() => void runPdf()}
              >
                {busy === "pdf" ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Export PDF
              </Button>
            ) : null}
          </div>
        </GridBand>

        {showRangeBar ? (
          <AnalyticsRangeBar
            value={range}
            onChange={onRangeChange}
            trailing={companyTrailing}
          />
        ) : companyTrailing ? (
          <GridCells className="grid-cols-1">{companyTrailing}</GridCells>
        ) : null}

        {children}
      </GridBlock>
    </div>
  );
}
